import React, { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { cnMerge } from '../../utils/twmerge'
import { FaMapMarkerAlt, FaGlobeAmericas } from 'react-icons/fa'

export interface LocationOption {
  value: string
  label: string
  cidade: string
  estado?: string
  pais?: string
  disabled?: boolean
}

export interface LocationSelectProps {
  options: LocationOption[]
  value?: string
  onChange?: (value: string) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  searchable?: boolean
  onSearch?: (query: string) => void
}

export const LocationSelect: React.FC<LocationSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = "Selecione uma localização...",
  className,
  disabled = false,
  searchable = true,
  onSearch
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedOption, setSelectedOption] = useState<LocationOption | null>(
    options.find(opt => opt.value === value) || null
  )
  const [dropdownCoords, setDropdownCoords] = useState({ top: 0, left: 0, width: 0 })
  const selectRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  const filteredOptions = searchable && searchQuery
    ? options.filter(option => 
        option.cidade.toLowerCase().includes(searchQuery.toLowerCase()) ||
        option.estado?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        option.pais?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        option.label.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : options

  // Sincronizar selectedOption quando value mudar externamente
  useEffect(() => {
    const newSelectedOption = options.find(opt => opt.value === value) || null
    setSelectedOption(newSelectedOption)
  }, [value, options])

  // Calcular posição do dropdown quando aberto
  useEffect(() => {
    if (isOpen && selectRef.current) {
      const rect = selectRef.current.getBoundingClientRect()
      const viewport = {
        width: window.innerWidth,
        height: window.innerHeight
      }
      
      const dropdownHeight = Math.min(300, filteredOptions.length * 56 + 40 + 8) // altura estimada
      const spaceBelow = viewport.height - rect.bottom
      const spaceAbove = rect.top
      
      let top = rect.bottom + window.scrollY + 4 // 4px gap
      
      // Se não há espaço suficiente abaixo, mostrar acima
      if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
        top = rect.top + window.scrollY - dropdownHeight - 4
      }
      
      setDropdownCoords({
        top,
        left: rect.left + window.scrollX,
        width: rect.width
      })
    }
  }, [isOpen, filteredOptions.length])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectRef.current && 
        !selectRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
        setSearchQuery('')
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
    
    return () => {}
  }, [isOpen])

  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [isOpen, searchable])

  useEffect(() => {
    if (searchable && onSearch) {
      onSearch(searchQuery)
    }
  }, [searchQuery, searchable, onSearch])

  const handleOptionClick = (option: LocationOption) => {
    if (option.disabled) return
    
    setSelectedOption(option)
    onChange?.(option.value)
    setIsOpen(false)
    setSearchQuery('')
  }

  return (
    <>
      <div className={cnMerge("relative", className)} ref={selectRef}>
        {/* Trigger Button */}
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={cnMerge(
            // Base styles
            'flex h-12 w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm',
            // Dark mode
            'dark:border-neutral-600 dark:bg-neutral-800 dark:text-gray-100',
            // Focus styles
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/20 focus-visible:border-blue-500',
            // Transition
            'transition-colors duration-200',
            // Disabled styles
            'disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-gray-50 dark:disabled:bg-neutral-700',
            // Hover
            'hover:border-gray-400 dark:hover:border-neutral-500',
            isOpen && 'border-blue-500 ring-2 ring-blue-500/20'
          )}
        >
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {selectedOption ? (
              <>
                {/* Icon */}
                <div className="flex-shrink-0">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30">
                    <FaMapMarkerAlt className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  </div>
                </div>
                {/* Location Info */}
                <div className="flex flex-col text-left min-w-0 flex-1">
                  <span className="text-gray-900 dark:text-gray-100 font-medium truncate">
                    {selectedOption.cidade}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {selectedOption.estado && selectedOption.pais 
                      ? `${selectedOption.estado}, ${selectedOption.pais}`
                      : selectedOption.estado || selectedOption.pais || ''}
                  </span>
                </div>
              </>
            ) : (
              <>
                <div className="flex-shrink-0">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 dark:bg-neutral-700">
                    <FaGlobeAmericas className="w-4 h-4 text-gray-400" />
                  </div>
                </div>
                <span className="text-gray-500 dark:text-gray-400">{placeholder}</span>
              </>
            )}
          </div>
          <svg
            className={cnMerge(
              'h-4 w-4 text-gray-400 dark:text-gray-500 transition-transform duration-200 flex-shrink-0',
              isOpen && 'rotate-180'
            )}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* Dropdown usando Portal */}
      {isOpen && createPortal(
        <div 
          ref={dropdownRef}
          className="fixed z-[9999] rounded-md border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 shadow-lg"
          style={{
            top: `${dropdownCoords.top}px`,
            left: `${dropdownCoords.left}px`,
            width: `${dropdownCoords.width}px`
          }}
        >
          {/* Search Input */}
          {searchable && (
            <div className="border-b border-gray-200 dark:border-neutral-700 p-2">
              <div className="relative">
                <FaMapMarkerAlt className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Buscar cidade, estado ou país..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full border-0 bg-transparent pl-9 pr-2 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-0"
                />
              </div>
            </div>
          )}

          {/* Options */}
          <div className="max-h-60 overflow-auto py-1">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleOptionClick(option)}
                  disabled={option.disabled}
                  className={cnMerge(
                    'flex w-full items-center gap-3 px-3 py-3 text-left transition-colors',
                    'hover:bg-gray-50 dark:hover:bg-neutral-700 focus:bg-gray-50 dark:focus:bg-neutral-700 focus:outline-none',
                    option.disabled && 'cursor-not-allowed opacity-50',
                    selectedOption?.value === option.value && 'bg-blue-50 dark:bg-blue-900/30'
                  )}
                >
                  {/* Icon */}
                  <div className="flex-shrink-0">
                    <div className={cnMerge(
                      "flex h-8 w-8 items-center justify-center rounded-full",
                      selectedOption?.value === option.value 
                        ? "bg-blue-100 dark:bg-blue-900/30" 
                        : "bg-gray-100 dark:bg-neutral-700"
                    )}>
                      <FaMapMarkerAlt className={cnMerge(
                        "w-4 h-4",
                        selectedOption?.value === option.value 
                          ? "text-blue-600 dark:text-blue-400" 
                          : "text-gray-500 dark:text-gray-400"
                      )} />
                    </div>
                  </div>
                  {/* Location Info */}
                  <div className="flex flex-col min-w-0 flex-1">
                    <span className={cnMerge(
                      "font-medium truncate",
                      selectedOption?.value === option.value 
                        ? 'text-blue-600 dark:text-blue-400' 
                        : 'text-gray-900 dark:text-gray-100'
                    )}>
                      {option.cidade}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {option.estado && option.pais 
                        ? `${option.estado}, ${option.pais}`
                        : option.estado || option.pais || ''}
                    </span>
                  </div>
                </button>
              ))
            ) : (
              <div className="px-3 py-8 text-center">
                <FaGlobeAmericas className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Nenhuma localização encontrada
                </p>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  )
}
